import { lazy } from "react";
import { Navigate } from "react-router-dom";

// project import
import Loadable from '@/components/Loadable';
import { HOME_PATH } from '@/config';
import MainLayout from '@/layout/MainLayout';
import NotificationPage from '@/components/NotificationPage';
import AuditLogsReport from '@/pages/Batch-Processing/AuditLogsReport';
import UserManagementDashboard from '@/pages/Batch-Processing/Configurations/UserManagementDashboard';
import CompanySettings from '@/pages/Batch-Processing/Configurations/CompanySettings';

// ── CPV ──────────────────────────────────────────────────────────────────────────────────────
const CpvHome = Loadable(lazy(() => import("@/pages/quality/cpv/CpvHome")));
const CpvSignalsBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/SignalsBoard")));
const CpvCapabilityBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/CapabilityBoard")));
const CpvPlansBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/PlansBoard")));
const CpvDataHealthBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/DataHealthBoard")));
const CpvManagementBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/ManagementBoard")));
const CpvRiskBoard = Loadable(lazy(() => import("@/pages/quality/cpv/dashboards/RiskBoard")));
const CpvRiskBands = Loadable(lazy(() => import("@/pages/quality/cpv/CpvRiskBands")));
const CpvPlans = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlans")));
const CpvPlanDetail = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlanDetail")));
const CpvPlanCharts = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlanCharts")));
const CpvParameterPage = Loadable(lazy(() => import("@/pages/quality/cpv/CpvParameterPage")));
const CpvMultivariate = Loadable(lazy(() => import("@/pages/quality/cpv/CpvMultivariate")));
const CpvSignals = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSignals")));
const CpvSignal = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSignal")));
const CpvMaster = Loadable(lazy(() => import("@/pages/quality/cpv/CpvMaster")));
const CpvMasterDetail = Loadable(lazy(() => import("@/pages/quality/cpv/CpvMasterDetail")));
const CpvMasterVersions = Loadable(lazy(() => import("@/pages/quality/cpv/CpvMasterVersions")));
const CpvSheets = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSheets")));
const CpvSheet = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSheet")));
const CpvSheetPrint = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSheetPrint")));
const CpvBatches = Loadable(lazy(() => import("@/pages/quality/cpv/CpvBatches")));
const CpvReports = Loadable(lazy(() => import("@/pages/quality/cpv/CpvReports")));
const CpvReport = Loadable(lazy(() => import("@/pages/quality/cpv/CpvReport")));
const CpvBatch = Loadable(lazy(() => import("@/pages/quality/cpv/CpvBatches").then((m) => ({ default: m.CpvBatch }))));
// CPV-NOEBMR-01 · My actions — every CPV act waiting on the signed-in person (GET /bp/cpv/my-actions/).
const CpvMyActions = Loadable(lazy(() => import("@/pages/quality/cpv/CpvMyActions")));
// G-09 · history imported from spreadsheets; G-43 · the statistics check
const CpvHistoryImports = Loadable(lazy(() => import("@/pages/quality/cpv/CpvHistoryImport")));
const CpvHistoryImportWizard = Loadable(lazy(() => import("@/pages/quality/cpv/CpvHistoryImport").then((m) => ({ default: m.CpvHistoryImportWizard }))));
const CpvHistoryImportDetail = Loadable(lazy(() => import("@/pages/quality/cpv/CpvHistoryImport").then((m) => ({ default: m.CpvHistoryImportDetail }))));
const CpvParityReport = Loadable(lazy(() => import("@/pages/quality/cpv/CpvParityReport")));
// CPV-AI-01 · the CPV assistant workspace (/bp/cpv-ai/…)
const CpvAssistant = Loadable(lazy(() => import("@/pages/quality/cpv/assistant/CpvAssistant")));
const CpvInsights = Loadable(lazy(() => import("@/pages/quality/cpv/insights/CpvInsights")));
// CPV Q7 · report schedules (Administration; POLICY cpv.report_schedule.configure)
const CpvReportSchedules = Loadable(lazy(() => import("@/pages/quality/cpv/schedules/CpvReportSchedules")));
// CPV Q6-P6 · validation pack (Administration; POLICY cpv.validation_pack.read)
const CpvValidationPack = Loadable(lazy(() => import("@/pages/quality/cpv/validation/CpvValidationPack")));
const CpvMethodSettings = Loadable(lazy(() => import("@/pages/quality/cpv/settings/CpvMethodSettings")));
const CpvRulesRegistry = Loadable(lazy(() => import("@/pages/quality/cpv/rules/CpvRulesRegistry")));
// CPV Q12-i · the CPV audit-trail review (Reports; POLICY cpv.audit_trail.review)
const CpvAuditTrailReview = Loadable(lazy(() => import("@/pages/quality/cpv/audit/CpvAuditTrailReview")));

// ── Master data ──────────────────────────────────────────────────────────────────────────────
const ProductConfig = Loadable(lazy(() => import("@/pages/Batch-Processing/Configurations/Product")));
const PlantConfiguration = Loadable(lazy(() => import("@/pages/Administration/PlantConfiguration")));
const MasterDataConfig = Loadable(lazy(() => import("@/pages/Batch-Processing/Configurations/MasterDataConfig")));

// ── Integrations ─────────────────────────────────────────────────────────────────────────────
const IntegrationsHub = Loadable(lazy(() => import("@/pages/Integrations/IntegrationsHub")));
const ConnectorFlows = Loadable(lazy(() => import("@/pages/Integrations/ConnectorFlows")));
const IntegrationStatus = Loadable(lazy(() => import("@/pages/Integrations/IntegrationStatus")));
const ConnectStudio = Loadable(lazy(() => import("@/pages/Integrations/connect/ConnectStudio")));

// ── Administration ───────────────────────────────────────────────────────────────────────────
const UserProfile = Loadable(lazy(() => import("@/components/UserProfile")));
const AccessControl = Loadable(lazy(() => import("@/pages/Batch-Processing/Configurations/AccessControl")));
const PasswordPolicy = Loadable(lazy(() => import("@/pages/Administration/PasswordPolicy")));
const ActiveSessions = Loadable(lazy(() => import("@/pages/Administration/Sessions/ActiveSessions")));
const SessionHistory = Loadable(lazy(() => import("@/pages/Administration/Sessions/SessionHistory")));
const UserSessionsView = Loadable(lazy(() => import("@/pages/Administration/Sessions/UserView")));
const AuditTrailReview = Loadable(lazy(() => import("@/pages/Administration/AuditTrailReview")));
const RecordExports = Loadable(lazy(() => import("@/pages/Administration/RecordExports")));
const ReportDesignerList = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerListRoute }))));
const ReportDesignerDesign = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerDesignRoute }))));
const ReportDesignerPreview = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerPreviewRoute }))));
const ReportDesignerPart = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerPartRoute }))));
const ApqrGraphStudio = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrGraphStudio")));
const ApqrDataTables = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrDataTables")));
const ApqrChecklists = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrChecklists")));
const ApqrChecklist = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrChecklist")));
const TemplateManager = Loadable(lazy(() => import("@/pages/TemplateManager")));
const ApplicationSettings = Loadable(lazy(() => import("@/pages/Batch-Processing/Configurations/ApplicationSettings")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = () => {
  return {
    path: "/",
    element: <MainLayout />,
    children: [
      { path: "/", element: <Navigate to={HOME_PATH} replace /> },
      { path: "/user-profile", element: <UserProfile /> },
      { path: "/notifications", element: <NotificationPage /> },

      // CPV
      { path: "/cpv", element: <CpvHome /> },
      { path: "/cpv/dashboards/signals", element: <CpvSignalsBoard /> },
      { path: "/cpv/dashboards/capability", element: <CpvCapabilityBoard /> },
      { path: "/cpv/dashboards/plans", element: <CpvPlansBoard /> },
      { path: "/cpv/dashboards/data-health", element: <CpvDataHealthBoard /> },
      { path: "/cpv/dashboards/management", element: <CpvManagementBoard /> },
      { path: "/cpv/dashboards/risk", element: <CpvRiskBoard /> },
      { path: "/cpv/risk-bands", element: <CpvRiskBands /> },
      { path: "/cpv/plans", element: <CpvPlans /> },
      { path: "/cpv/plans/:planId", element: <CpvPlanDetail /> },
      { path: "/cpv/plans/:id/charts", element: <CpvPlanCharts /> },
      { path: "/cpv/plans/:planId/charts/:itemId", element: <CpvParameterPage /> },
      { path: "/cpv/plans/:id/multivariate", element: <CpvMultivariate /> },
      { path: "/cpv/signals", element: <CpvSignals /> },
      { path: "/cpv/signals/:id", element: <CpvSignal /> },
      { path: "/cpv/master", element: <CpvMaster /> },
      { path: "/cpv/master/:productId", element: <CpvMasterDetail /> },
      { path: "/cpv/master/:productId/versions", element: <CpvMasterVersions /> },
      { path: "/cpv/sheets", element: <CpvSheets /> },
      { path: "/cpv/sheets/:id", element: <CpvSheet /> },
      { path: "/cpv/sheets/:id/print", element: <CpvSheetPrint /> },
      { path: "/cpv/batches", element: <CpvBatches /> },
      { path: "/cpv/batches/:id", element: <CpvBatch /> },
      { path: "/cpv/batches/imports", element: <CpvHistoryImports /> },
      { path: "/cpv/batches/imports/new", element: <CpvHistoryImportWizard /> },
      { path: "/cpv/batches/imports/:id", element: <CpvHistoryImportDetail /> },
      { path: "/cpv/reports/statistics-check", element: <CpvParityReport /> },
      { path: "/cpv/reports", element: <CpvReports /> },
      { path: "/cpv/reports/:id", element: <CpvReport /> },
      { path: "/cpv/my-actions", element: <CpvMyActions /> },
      { path: "/cpv/assistant", element: <CpvAssistant /> },
      { path: "/cpv/insights", element: <CpvInsights /> },
      { path: "/cpv/report-schedules", element: <CpvReportSchedules /> },
      { path: "/cpv/validation-pack", element: <CpvValidationPack /> },
      { path: "/cpv/audit-trail", element: <CpvAuditTrailReview /> },
      { path: "/cpv/method-settings", element: <CpvMethodSettings /> },
      { path: "/cpv/rules", element: <CpvRulesRegistry /> },
      { path: "/my-actions", element: <Navigate to="/cpv/my-actions" replace /> },

      // Master data
      { path: "/prod-config", element: <ProductConfig /> },
      { path: "/plant-configuration", element: <PlantConfiguration /> },
      { path: "/master-config", element: <MasterDataConfig /> },

      // Integrations
      { path: "/system-integration", element: <IntegrationsHub /> },
      { path: "/integrations/status", element: <IntegrationStatus /> },
      { path: "/integrations/connect", element: <ConnectStudio /> },
      { path: "/integrations/:system", element: <ConnectorFlows /> },

      // Administration
      { path: "/user-management", element: <UserManagementDashboard /> },
      { path: "/accessControl", element: <AccessControl /> },
      { path: "/password-policy", element: <PasswordPolicy /> },
      { path: "/active-sessions", element: <ActiveSessions /> },
      { path: "/session-history", element: <SessionHistory /> },
      { path: "/user-view/:userId", element: <UserSessionsView /> },
      { path: "/audit-logs", element: <AuditLogsReport /> },
      { path: "/audit-trail-review", element: <AuditTrailReview /> },
      { path: "/record-exports", element: <RecordExports /> },
      { path: "/report-designer", element: <ReportDesignerList /> },
      { path: "/report-designer/versions/:versionId", element: <ReportDesignerDesign /> },
      { path: "/report-designer/versions/:versionId/preview", element: <ReportDesignerPreview /> },
      { path: "/report-designer/parts/:partId", element: <ReportDesignerPart /> },
      { path: "/analytics-studio", element: <ApqrGraphStudio /> },
      { path: "/graph-studio", element: <Navigate to="/analytics-studio" replace /> },
      { path: "/data-tables", element: <ApqrDataTables /> },
      // CPV-Q6 S6: configurable checklists (apqr/services/checklists)
      { path: "/checklists", element: <ApqrChecklists /> },
      { path: "/checklists/:id", element: <ApqrChecklist /> },
      { path: "/workflow-design", element: <TemplateManager /> },
      { path: "/app-setting", element: <ApplicationSettings /> },
      { path: "/company-settings", element: <CompanySettings /> },

      { path: "*", element: <Navigate to={HOME_PATH} replace /> },
    ],
  };
};

export default MainRoutes;
